import { useState, useRef, memo, useMemo, MouseEvent, useEffect } from 'react';
import { format, addMinutes, isSameDay } from 'date-fns';
import { DragOverlay, useDraggable, useDroppable, defaultDropAnimation } from '@dnd-kit/core';
import { Undo2 } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDrag, usePlanner } from "../contexts/PlannerContext";
import { useCourses } from "@/lib/courses";
import { useCommunication } from "@/lib/CommunicationWrapper";
import { DoneSvg, ProgressSvg, IncompleteSvg } from '@/lib/svg';
import Color from 'color';
import { AssignmentPlanned } from '@/lib/types';
import { defaultTaskValues } from '@/lib/types';
import { formatTime } from '@/lib/utils';
import { DueItemContent } from './DueView';
import { isTakeHomeAssignment } from "../utils/planningType";

export const CalendarVars = () => {

    const { blockHeight, blockWidth, timeWidth, blockMargin, dueBlockWidth, view, cursorType } = usePlanner();

    return (
        <style>
            {`
                html {
                    --block-height: ${blockHeight}px;
                    --block-height-1: ${blockHeight - 1}px;
                    --block-width: ${blockWidth}px;
                    --due-block-width: ${dueBlockWidth}px;
                    --time-width: ${timeWidth}px;
                    --block-margin: ${blockMargin}px;
                    cursor: ${cursorType || 'default'};
                }

            `}
        </style>
    )
}

export const CalendarTimeIndicators = memo(() => {

    const times = Array.from({ length: 25 }, (_, i) => {
        const time = format(new Date(0, 0, 0, i), 'h:mmaaa');
        return <CalendarTime time={time} key={"time-indicator-" + i} last={i === 24} />
    })

    return (
        <div className="flex flex-col w-max mt-[20px]">
            {times}
            <p className="opacity-0">88:88AM</p>
        </div>
    )
})

export const CalendarColumnHeader = memo(({ date, today }: { date: Date, today: boolean }) => {

    return (
        <div className="flex-1 flex items-center justify-center gap-[5px] min-w-[var(--block-width)] max-w-[var(--block-width)]">
            <p className="font-medium">{format(date, 'eee')}</p>
            <p className="text-theme-1">{format(date, 'MMM d')}</p>
            {today && <p className="text-sm text-theme-link font-medium">Today</p>}
        </div>
    )
})

export const CalendarRowLines = memo(() => {

    const { blockHeight } = usePlanner();

    const lines = useMemo(() => Array.from({ length: 25 }, (_, i) => {
        return <div className="absolute w-full h-[1px] border-b border-theme" style={{ "top": (i * blockHeight) + 'px' }} key={"row-line-" + i} />
    }), [blockHeight]);

    return (
        <div className={`absolute top-0 left-0 w-full h-full z-1 mt-[20px]`}>
            {lines}
        </div>
    )
})


export const NonCalendarDragPreviewContent = ({ item }: { item: any }) => {

    const { dueItems, blockHeight, overrides } = usePlanner();
    const { courseData } = useCourses();

    // Set minimum height of 20px for very short events
    const height = Math.max(((item?.duration || 30) / 60) * blockHeight, 20);

    const course = courseData?.[item?.course];
    const color = course?.color || "gray";

    // Determine if color is dark or light and set appropriate text color
    const textColor = useMemo(() => {
        try {
            const colorObj = Color(color);
            const lightness = colorObj.lightness();
            return lightness > 50 ? "#000" : "#fff";
        } catch (error) {
            return "#000000";
        }
    }, [color]);

    if (!item) return null;

    return (
        <div className="opacity-50 bg-theme-1 rounded-md">
            <DueItemContent item={{ "current": item, "original": item }} onAutoSchedule={() => {}} />
        </div>
    )

}
export const NonCalendarDragPreview = () => {

    const { activeDragItem, shouldAnimateDrop } = useDrag();
    const { settings } = usePlanner();
    const isTasksView = (settings?.assignments_view || "planner") === "due";

    const dropAnimation = useMemo(() => {
        return isTasksView && shouldAnimateDrop ? defaultDropAnimation : null;
    }, [isTasksView, shouldAnimateDrop]);

    return (
        <DragOverlay dropAnimation={dropAnimation}>
            <NonCalendarDragPreviewContent item={activeDragItem} />
        </DragOverlay>
    )
}

export const CalendarDragPreview = () => {
    const { getRowFromDelta, blockHeight, settings } = usePlanner();
    const { activeCalendarDragItem, currentDragDelta, resizeHeight } = useDrag();
    const { courseData } = useCourses();

    if (!activeCalendarDragItem) return null;

    // Use useMemo to prevent unnecessary re-renders

    const item = activeCalendarDragItem;

    const delta = useMemo(() => {
        if (currentDragDelta) {
            return currentDragDelta;
        } else if (item?.plannedAt) {
            const date = new Date(item.plannedAt);
            return date;
        }
        return null; // Provide a default return
    }, [currentDragDelta, item]);


    if (!delta) return null;

    const row = getRowFromDelta(delta);
    // Set minimum height of 20px for very short events
    const calculatedHeight = ((item?.duration || defaultTaskValues.duration) / 60) * blockHeight;
    const height = resizeHeight || Math.max(calculatedHeight, 20);

    const startDate = new Date(delta);
    const endDate = addMinutes(startDate, (item?.duration || defaultTaskValues.duration));

    const course = courseData?.[item?.course] || {};
    const color = course?.color || "gray";
    const backgroundImage = course?.img;
    const hasBackgroundImage = backgroundImage && backgroundImage !== "";
    const backgroundStyle = hasBackgroundImage ? { background: `linear-gradient(${color}dd, ${color}cc), url('${backgroundImage}')`, "backgroundPosition": "center", "backgroundSize": "cover", "backgroundRepeat": "no-repeat" } : { "background": color };

    console.log("color of the dragging preview", color, course, item);

    const textColor = useMemo(() => {
        try {
            const colorObj = Color(color);
            const lightness = colorObj.lightness();
            return lightness > 50 ? "#000" : "#fff";
        } catch (error) {
            return "#000000";
        }
    }, [color]);

    const isShortEvent = item.duration <= 15;

    const lineClamp = getLineClamp(item?.duration || 0);

    const zoomScale = (settings.assignments_zoom || 100) / 100;
    const titleFontSize = Math.max(8, isShortEvent ? Math.round(11 * zoomScale) : Math.round(12 * zoomScale));
    const titleLineHeight = Math.max(9, isShortEvent ? Math.round(12 * zoomScale) : Math.round(13 * zoomScale));
    const timeFontSize = Math.max(7, Math.round(10 * zoomScale));

    return (
        <div key={item.title} className="absolute left-0 w-full bg-theme-0 p-[2px] rounded-lg cursor-grab" style={{ "top": (row * blockHeight) + 'px', "height": height + 'px' }}>
            <div className="overflow-hidden max-h-full h-full flex items-start gap-1 bg-theme-1 rounded-md px-[5px] relative z-10">
                <div className="absolute top-0 left-0 w-full h-full rounded-md z-[-1]" style={backgroundStyle}></div>
                <button className="mt-1 cursor-pointer flex-shrink-0 opacity-75 hover:scale-105 hover:opacity-100 transition-all duration-200" style={{ color: textColor }}>
                    {item.status === "in-progress" ?
                        <ProgressSvg className="w-[14px] h-[14px]" /> :
                        item.status === "complete" || item.status === "graded" ?
                            <DoneSvg className="w-[14px] h-[14px]" /> :
                            <IncompleteSvg className="w-[14px] h-[14px]" />
                    }
                </button>
                <div className="flex-1 min-w-0 overflow-hidden" style={{ color: textColor }}>
                    <div className="flex items-start gap-1">
                        <p className={`font-semibold flex-1 overflow-hidden ${item.status === "complete" || item.status === "graded" ? "line-through" : ""}`} style={{ fontSize: `${titleFontSize}px`, lineHeight: `${titleLineHeight}px`, display: '-webkit-box', WebkitLineClamp: lineClamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{item.title}</p>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button className="shrink-0 cursor-pointer hover:scale-105 transition-all duration-200">
                                    <Undo2 className="w-4 h-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="mb-2">
                                <span>Remove from calendar</span>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-[5px] overflow-hidden whitespace-nowrap" style={{ fontSize: `${timeFontSize}px`, lineHeight: `${timeFontSize + 2}px`, color: textColor }}>
                        <p>{formatTime(startDate, 'h:mmaaa')}</p>
                        <span>-</span>
                        <p>{formatTime(endDate, 'h:mmaaa')}</p>
                    </div>
                </div>
            </div>
        </div >
    )
}

export const CalendarTime = memo(({ time, last }: { time: string, last: boolean }) => {

    return (
        <div className={`relative border-t border-theme ${last ? "h-2" : "h-[var(--block-height)]"}`}>
            <p className={`absolute top-[-10px] right-[5px] bg-theme-0 text-theme-1 text-sm`}>{time}</p>
        </div>
    )
})


export const CalendarColumn = ({ start, items, today, scheduleItems, dueItems, calendarEvents, compactDuePopover = false, showTimeIndicator = false }: { start: Date, items: AssignmentPlanned[], today: boolean, scheduleItems: any[], dueItems: any[], calendarEvents: any[], compactDuePopover?: boolean, showTimeIndicator?: boolean }) => {

    const { courseData } = useCourses();

    const { activeCalendarDragItem, currentDragDelta, resizeHeight, activeDragItem } = useDrag();
    const { blockHeight, scrollArea, settings } = usePlanner();
    const [viewportHeight, setViewportHeight] = useState(0);
    const columnRef = useRef<HTMLDivElement>(null);

    const { setNodeRef, isOver } = useDroppable({
        id: start.toISOString(),
        data: {
            "type": "calendar-column",
            "day": start.getDate(),
            "month": start.getMonth(),
            "year": start.getFullYear(),
        }
    });

    const renderDueItems = () => {
        if (!dueItems) return null;

        // Helper function to get exact minute (no rounding)
        const getExactMinute = (date: Date) => {
            const exactDate = new Date(date);
            exactDate.setSeconds(0, 0);
            return exactDate;
        };

        // Group items by exact minute
        const groupedItems: Record<string, any[]> = {};

        const calendarViewType = settings?.assignments_calendar_view_type || "week";

        const filteredDue: any[] = [];
        const seenIds = new Set<string>();

        const isWithinSchedule = (date: Date | null, sched: any) => {
            if (!date) return false;
            if (!isSameDay(date, start)) return false;
            const minutes = (date.getHours() * 60) + date.getMinutes();
            return minutes >= sched.start && minutes <= sched.end;
        };

        for (const dueItem of dueItems) {
            // Take-home items belong only in the due bar, never in the calendar grid
            if (isTakeHomeAssignment(dueItem)) continue;

            const dueDate = dueItem?.current?.due ? new Date(dueItem.current.due) : null;
            if (!dueDate || !isSameDay(dueDate, start)) continue;

            const id = dueItem?.current?.id ?? dueItem?.original?.id;
            if (id !== undefined && id !== null) {
                const key = String(id);
                if (seenIds.has(key)) continue;
                seenIds.add(key);
            }

            const plannedDate = dueItem?.current?.plannedAt ? new Date(dueItem.current.plannedAt) : null;
            const inClass = scheduleItems?.some((sched: any) => {
                return isWithinSchedule(dueDate, sched) || isWithinSchedule(plannedDate, sched);
            });
            if (inClass) continue;

            filteredDue.push(dueItem);
        }

        filteredDue.forEach((item: any) => {
            if (!item.current.due) return;
            const itemTime = new Date(item.current.due);
            const exactTime = getExactMinute(itemTime);
            const timeKey = exactTime.toISOString();

            if (!groupedItems[timeKey]) {
                groupedItems[timeKey] = [];
            }
            groupedItems[timeKey].push(item);
        });

        // Convert grouped items to array and sort by time
        const sortedGroups = Object.entries(groupedItems)
            .sort(([timeKeyA], [timeKeyB]) => timeKeyA.localeCompare(timeKeyB));

        return sortedGroups.map(([timeKey, items]) => {
            const groupTime = new Date(timeKey);
            const groupHour = groupTime.getHours();
            const groupMinute = groupTime.getMinutes();
            const groupTop = (groupHour + (groupMinute / 60)) * blockHeight;

            return (
                <DueItemGroup
                    key={timeKey}
                    items={items}
                    top={groupTop}
                />
            );
        });
    }

    const renderItems = () => {

        if (!items) return null;

        const isWithinClass = (plannedItem: AssignmentPlanned) => {
            if (!plannedItem?.plannedAt) return false;
            const plannedDate = new Date(plannedItem.plannedAt);
            const plannedMinutes = (plannedDate.getHours() * 60) + plannedDate.getMinutes();
            const plannedEnd = plannedMinutes + (plannedItem.duration || defaultTaskValues.duration);
            return scheduleItems?.some((sched: any) => {
                return plannedMinutes < sched.end && plannedEnd > sched.start;
            });
        };

        const visibleItems = items.filter((item: AssignmentPlanned) => !isWithinClass(item));

        return visibleItems.map((item: AssignmentPlanned) => (
            <CalendarItem key={item?.id} item={item} />
        ));
    }

    const renderScheduleItems = () => {
        if (!scheduleItems) return null;
        const portalContainer = (scrollArea?.current as HTMLElement | null) || undefined;
        return scheduleItems.map((item: any) => {
            const classStartMinutes = item.start;
            const classEndMinutes = item.end;
            const isWithinClassWindow = (date: Date | null) => {
                if (!date) return false;
                if (
                    date.getFullYear() !== start.getFullYear() ||
                    date.getMonth() !== start.getMonth() ||
                    date.getDate() !== start.getDate()
                ) {
                    return false;
                }
                const minutes = (date.getHours() * 60) + date.getMinutes();
                return minutes >= classStartMinutes && minutes <= classEndMinutes;
            };

            const seenDueIds = new Set<string>();
            const dueInsideClass = dueItems.filter((dueItem: any) => {
                const dueDate = dueItem?.current?.due ? new Date(dueItem.current.due) : null;

                // Only surface in-class due items when the actual due time overlaps the class window
                const dueDuringClass = isWithinClassWindow(dueDate);
                const isInClassType = !isTakeHomeAssignment(dueItem);
                if (!dueDuringClass || !isInClassType) return false;

                // Ensure only one entry per assignment id inside the class popover
                const id = dueItem?.current?.id ?? dueItem?.original?.id;
                if (id !== undefined && id !== null) {
                    const key = String(id);
                    if (seenDueIds.has(key)) return false;
                    seenDueIds.add(key);
                }

                return true;
            });

            return (
                <CalendarScheduleItem
                    key={item.course}
                    item={item}
                    course={courseData[item.course]}
                    dueItemsWithin={dueInsideClass}
                    portalContainer={portalContainer}
                    compact={compactDuePopover}
                />
            );
        });
    }

    const renderCalendarEvents = () => {
        if (!calendarEvents) return null;
        return calendarEvents.map((item: any) => {
            // Use GoogleCalendarItem for both Google and Microsoft events for now
            // Could create separate components later if needed
            return <GoogleCalendarItem key={item.id} item={item} />
        });
    }

    const shouldShowPreview = useMemo(() => {
        if (!activeCalendarDragItem) return false;
        if (!currentDragDelta && !resizeHeight) return false;
        if (currentDragDelta.getFullYear() !== start.getFullYear()) return false;
        if (currentDragDelta.getMonth() !== start.getMonth()) return false;
        if (currentDragDelta.getDate() !== start.getDate()) return false;
        return true;
    }, [activeCalendarDragItem, currentDragDelta, resizeHeight]);

    return (
        <div
            className="relative flex-1 min-w-[var(--block-width)] max-w-[var(--block-width)] mt-[20px]"
            ref={(node) => {
                setNodeRef(node);
                if (node) {
                    columnRef.current = node;
                }
            }}
        >
            {activeDragItem && (
                <div className="overflow-hidden m-1 absolute inset-0 border-2 border-dashed border-theme-link opacity-50 rounded-lg pointer-events-none z-10">
                    <div className="absolute top-0 left-0 w-full h-full opacity-20 z-[10] bg-theme-link"></div>
                </div>
            )}
            {renderDueItems()}
            {renderItems()}
            {renderScheduleItems()}
            {renderCalendarEvents()}
            {shouldShowPreview && <CalendarDragPreview />}
            {(showTimeIndicator || today) && <TodayIndicator />}
        </div>
    )
}

const DueItemGroup = ({ items, top }: { items: any[], top: number }) => {

    const { blockHeight, settings, scrollArea } = usePlanner();
    const { courseData } = useCourses();
    const { loadSharedComponent } = useCommunication();

    const maxVisible = items.length <= 2 ? items.length : 1;
    const visibleItems = items.slice(0, maxVisible);
    const overflowCount = Math.max(items.length - visibleItems.length, 0);

    const zoomScale = (settings.assignments_zoom || 100) / 100;
    const labelFontSize = Math.max(10, Math.round(11 * zoomScale));
    const chipHeight = Math.max(26, Math.round(blockHeight * 0.22));
    const rowHeight = Math.max(chipHeight, Math.round(blockHeight * 0.25));
    const totalVisibleRows = visibleItems.length + (overflowCount > 0 ? 1 : 0);
    const containerHeight = Math.max(
        rowHeight,
        (totalVisibleRows * chipHeight) + (Math.max(totalVisibleRows - 1, 0) * 6)
    );

    const getCourseColor = (courseId: string) => courseData?.[courseId]?.color || "gray";

    const normalizeAssignment = (item: any) => {
        if (item?.current && item?.original) {
            return {
                current: { ...item.current },
                original: { ...item.original },
            };
        }
        const base = item?.current || item || {};
        return {
            current: {
                id: base.id,
                title: base.title,
                course: base.course,
                due: base.due,
                plannedAt: base.plannedAt,
                duration: base.duration || defaultTaskValues.duration,
                status: base.status,
                type: base.type,
                points: base.points,
                score: base.score,
                url: base.url,
                numLinks: base.numLinks,
            },
            original: {
                id: base.id,
                title: base.title,
                course: base.course,
                due: base.due,
                plannedAt: base.plannedAt,
                duration: base.duration || defaultTaskValues.duration,
                status: base.status,
                type: base.type,
                points: base.points,
                score: base.score,
                url: base.url,
                numLinks: base.numLinks,
            }
        };
    };

    const handleContainerClick = () => {
        const first = items[0];
        if (first) loadSharedComponent("AssignmentCreator", { assignment: normalizeAssignment(first) });
    };

    const renderChip = (item: any, keySuffix: string) => {
        const courseColor = getCourseColor(item?.current?.course || "");
        const dueTime = item?.current?.due ? format(new Date(item.current.due), 'h:mmaaa') : "";

        let background = "rgba(0,0,0,0.04)";
        try {
            background = Color(courseColor).alpha(0.14).string();
        } catch (error) {
            background = "rgba(0,0,0,0.04)";
        }

        return (
            <button
                key={(item?.current?.id || item?.current?.title || "due-item") + keySuffix}
                data-due-chip="true"
                className="flex items-center gap-[6px] rounded-md px-[8px] py-[4px] border border-theme bg-theme-0 text-theme-0 text-left w-full min-w-0 cursor-pointer hover:brightness-95 transition-all duration-150"
                style={{ borderColor: courseColor, background }}
                onClick={(e) => {
                    e.stopPropagation();
                    loadSharedComponent("AssignmentCreator", { assignment: normalizeAssignment(item) });
                }}
                onMouseDown={(e) => {
                    e.stopPropagation();
                    if (e.button === 0) loadSharedComponent("AssignmentCreator", { assignment: normalizeAssignment(item) });
                }}
            >
                <span className="h-[10px] w-[10px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                <span className="flex-1 min-w-0 font-semibold truncate" style={{ fontSize: `${labelFontSize}px` }}>
                    {item?.current?.title || 'Due item'}
                </span>
                {dueTime && <span className="text-[11px] text-theme-1 whitespace-nowrap">{dueTime}</span>}
            </button>
        )
    };

    return (
        <div
            className="absolute left-0 w-full px-[4px] z-10 pointer-events-auto"
            style={{
                top: `${top}px`,
                minHeight: `${containerHeight}px`,
                cursor: "pointer"
            }}
            onClick={handleContainerClick}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleContainerClick();
                }
            }}
            role="button"
            tabIndex={0}
        >
            <div className="flex items-center gap-[8px] overflow-hidden">
                <div className="flex flex-col gap-[6px] flex-1 min-w-0">
                    {visibleItems.map((item, idx) => (
                        <div key={idx} style={{ minHeight: `${chipHeight}px` }}>
                            {renderChip(item, "-visible-" + idx)}
                        </div>
                    ))}
                    {overflowCount > 0 && (
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className="flex items-center justify-between rounded-md px-[10px] py-[5px] border border-theme text-xs font-semibold text-theme-link bg-theme-0 hover:bg-theme-1 transition-all duration-150 w-full"
                                    style={{ minHeight: `${chipHeight}px` }}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onMouseUp={(e) => e.stopPropagation()}
                                >
                                    +{overflowCount} more
                                </button>
                            </PopoverTrigger>
                            <PopoverContent
                                side="bottom"
                                align="start"
                                sideOffset={8}
                                avoidCollisions={false}
                                className="w-[320px] p-[12px]"
                                zIndex={1}
                                container={(scrollArea?.current as HTMLElement | null) || undefined}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseUp={(e) => e.stopPropagation()}
                            >
                                <div className="flex items-center justify-between mb-[8px]">
                                    <p className="text-sm font-semibold">Due items</p>
                                    <p className="text-xs text-theme-1">{items.length} total</p>
                                </div>
                                <div className="flex flex-col gap-[8px]">
                                    {items.map((item, idx) => (
                                        <div key={idx} onClick={() => loadSharedComponent("AssignmentCreator", { assignment: normalizeAssignment(item) })}>
                                            {renderChip(item, "-list-" + idx)}
                                        </div>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                    )}
                </div>
                {overflowCount > 0 && (
                    <div className="hidden" />
                )}
            </div>
        </div>
    );
};

const GoogleSvg = ({ className }: { className: string }) => {

    return (
        <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className={className}><title>Google</title><path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" /></svg>
    )
}

export const MicrosoftSvg = ({ className }: { className: string }) => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className={className}><path d="M0 0h15.206v15.206H0z" /><path d="M16.794 0H32v15.206H16.794z" /><path d="M0 16.794h15.206V32H0z" /><path d="M16.794 16.794H32V32H16.794z" /></svg>)
}

const getLineClamp = (duration: number) => {
    if (duration <= 40) return 1;
    if (duration <= 55) return 2;
    if (duration <= 70) return 3;
    return 4;
}

export const GoogleCalendarItem = ({ item }: { item: any }) => {

    const { blockHeight, subBlocks, settings } = usePlanner();

    const getRowFromTime = (time: string) => {

        const date = new Date(time);

        const nearestSubBlock = Math.floor(date?.getMinutes() / (60 / subBlocks));
        const row = date?.getHours() + (nearestSubBlock / subBlocks);

        return row;

    }

    const color = item.calendarColor;


    const textColor = useMemo(() => {
        try {
            const colorObj = Color(color);
            const lightness = colorObj.lightness();
            // If luminance is high (closer to 1), the color is light, so use dark text
            // If luminance is low (closer to 0), the color is dark, so use light text
            return lightness > 50 ? "#000" : "#fff";
        } catch (error) {
            // Fallback to black text if color parsing fails
            return "#000000";
        }
    }, [color]);

    const startTime = new Date(item.plannedAt ?? "");
    const endTime = addMinutes(startTime, item.duration);

    const isShortEvent = item.duration <= 15;
    // Set minimum height of 20px for 0-duration events (like Google Calendar)
    const eventHeight = item.duration === 0 ? 20 : Math.max((item.duration / 60) * blockHeight, 20);

    // Calculate line clamp based on available height

    const lineClamp = getLineClamp(item?.duration || 0);

    const zoomScale = (settings.assignments_zoom || 100) / 100;
    const titleFontSize = Math.max(8, Math.round(12 * zoomScale));
    const titleLineHeight = Math.max(9, Math.round(13 * zoomScale));
    const timeFontSize = Math.max(7, Math.round(11 * zoomScale));
    const locationFontSize = Math.max(8, Math.round(12 * zoomScale));

    return (
        <div
            className="absolute left-0 w-full bg-theme-0 p-[2px] rounded-lg"
            style={{
                top: (getRowFromTime(item.plannedAt) * blockHeight) + 'px',
                height: eventHeight + 'px',
                cursor: item.htmlLink ? 'pointer' : 'default'
            }}
            onClick={() => {
                if (item.htmlLink) {
                    window.open(item.htmlLink, '_blank');
                }
            }}
        >
            <div className="overflow-hidden max-h-full h-full flex flex-col bg-theme-1 rounded-md px-[5px] relative z-10">
                <div className="absolute top-0 left-0 w-full h-full bg-theme-1 rounded-md z-[-1]" style={{ background: color }}></div>
                <div className="flex-1 min-w-0 overflow-hidden" style={{ color: textColor }}>
                    <div className="flex items-start justify-between gap-1">
                        <p className="font-semibold flex-1 overflow-hidden" style={{ fontSize: `${titleFontSize}px`, lineHeight: `${titleLineHeight}px`, display: '-webkit-box', WebkitLineClamp: lineClamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{item.title}</p>
                        {item.isGoogleEvent && <GoogleSvg className="w-[12px] h-[12px] flex-shrink-0" />}
                        {item.isMicrosoftEvent && <MicrosoftSvg className="w-[12px] h-[12px] flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-[5px] overflow-hidden whitespace-nowrap" style={{ fontSize: `${timeFontSize}px`, lineHeight: `${timeFontSize + 2}px`, color: textColor }}>
                        <p>{formatTime(startTime, 'h:mmaaa')}</p>
                        <span>-</span>
                        <p>{formatTime(endTime, 'h:mmaaa')}</p>
                    </div>
                    {!isShortEvent && item.location && <p className="opacity-60 truncate" style={{ fontSize: `${locationFontSize}px`, lineHeight: `${locationFontSize + 2}px` }}>{item.location}</p>}
                </div>
            </div>
        </div>
    );
}

export const CalendarItem = ({ item }: { item: AssignmentPlanned }) => {


    if (!item) return null;

    //const isGoogleEvent = item.isGoogleEvent || false;

    const { attributes, listeners, setNodeRef } = useDraggable({
        id: "calendar_item_" + item?.id,
        data: {
            "type": "calendar-item",
            "planner": {
                "id": item?.id,
                "plannedAt": item?.plannedAt,
                "duration": item?.duration || defaultTaskValues.duration,
                "title": item?.title,
                "course": item?.course,
                "status": item?.status,
            }
        },
        //disabled: isGoogleEvent // Disable dragging for Google Calendar events
    });

    const { courseData } = useCourses();
    const { blockHeight, subBlocks, removeFromCalendar, settings, dueItems, modifyOverrides, modifyPlanned } = usePlanner();
    const { activeCalendarDragItem } = useDrag();
    const { loadSharedComponent } = useCommunication();

    const getRowFromTime = (time: string) => {

        const date = new Date(time);

        const nearestSubBlock = Math.floor(date?.getMinutes() / (60 / subBlocks));
        const row = date?.getHours() + (nearestSubBlock / subBlocks);

        return row;

    }

    const DraggableBottom = () => {

        const { attributes, listeners, setNodeRef } = useDraggable({
            id: "resize_bottom_" + item.id,
            data: {
                "type": "calendar-item-resize-bottom",
                "planner": {
                    "id": item.id,
                    "title": item.title,
                    "course": item.course,
                    "plannedAt": item.plannedAt,
                    "duration": item.duration || defaultTaskValues.duration,
                    "status": item.status,
                }
            }
        });

        return (
            <div ref={setNodeRef} {...listeners} {...attributes} className="opacity-0 absolute -bottom-[0px] h-[10px] w-full bg-theme-1 rounded-lg cursor-ns-resize z-10"></div>
        )
    }

    const DraggableTop = () => {

        const { attributes, listeners, setNodeRef } = useDraggable({
            id: "resize_top_" + item.id,
            data: {
                "type": "calendar-item-resize-top",
                "planner": {
                    "id": item.id,
                    "title": item.title,
                    "course": item.course,
                    "plannedAt": item.plannedAt,
                    "duration": item.duration || defaultTaskValues.duration,
                    "status": item.status,
                }
            }
        });

        return (
            <div ref={setNodeRef} {...listeners} {...attributes} className="opacity-0 absolute -top-[0px] h-[10px] w-full bg-theme-1 rounded-lg cursor-ns-resize z-10"></div>
        )
    }

    const course = courseData?.[item.course];
    const backgroundImage = course?.img;
    const color = (course?.color || "gray");
    const hasBackgroundImage = backgroundImage && backgroundImage !== "";
    const backgroundStyle = hasBackgroundImage ? { background: `linear-gradient(${color}dd, ${color}cc), url('${backgroundImage}')`, "backgroundPosition": "center", "backgroundSize": "cover", "backgroundRepeat": "no-repeat" } : { "background": color };
    const [localStatus, setLocalStatus] = useState(item?.status || "incomplete");

    useEffect(() => {
        setLocalStatus(item?.status || "incomplete");
    }, [item?.status]);

    // Determine if color is dark or light and set appropriate text color
    const textColor = useMemo(() => {
        try {
            const colorObj = Color(color);
            const lightness = colorObj.lightness();
            // If luminance is high (closer to 1), the color is light, so use dark text
            // If luminance is low (closer to 0), the color is dark, so use light text
            return lightness > 50 ? "#000" : "#fff";
        } catch (error) {
            // Fallback to black text if color parsing fails
            return "#000000";
        }
    }, [color]);

    const startTime = new Date(item.plannedAt ?? "");
    const endTime = addMinutes(startTime, item.duration);

    /*
    if (isGoogleEvent) {
        // Render Google Calendar events with a distinct style and no drag handles
        const isShortEvent = item.duration <= 15;
        // Set minimum height of 20px for 0-duration events (like Google Calendar)
        const eventHeight = item.duration === 0 ? 20 : Math.max((item.duration / 60) * blockHeight, 20);

        return (
            <div
                className="absolute left-0 w-full bg-theme-0 p-[3px] rounded-lg"
                style={{
                    top: (getRowFromTime(item.plannedAt) * blockHeight) + 'px',
                    height: eventHeight + 'px',
                    cursor: item.htmlLink ? 'pointer' : 'default'
                }}
                onClick={() => {
                    if (item.htmlLink) {
                        window.open(item.htmlLink, '_blank');
                    }
                }}
            >
                <div className="overflow-hidden max-h-full h-full flex flex-col bg-theme-1 rounded-lg px-[5px] relative z-10 border-l-4" style={{ borderLeftColor: color }}>
                    <div className="absolute top-0 left-0 w-full h-full bg-theme-1 rounded-lg opacity-20 z-[-1]" style={{ background: color }}></div>
                    {/* Google Calendar icon *}
                    <img
                        src={GoogleIcon}
                        alt="Google Calendar"
                        className="absolute top-[4px] right-[4px] h-[12px] w-[12px] opacity-60 bc-invert"
                    />
                    {isShortEvent ? (
                        // For events ≤15 minutes: title and time inline, no location
                        <div className="flex items-center justify-between w-full h-full">
                            <p className="text-sm font-semibold truncate flex-1">{item.title}</p>
                            <div className="flex items-center gap-[2px] text-xs opacity-75 ml-[5px] whitespace-nowrap">
                                <p>{formatTime(new Date(item.plannedAt), 'h:mmaaa')}</p>
                                <span>-</span>
                                <p>{formatTime(endTime, 'h:mmaaa')}</p>
                            </div>
                        </div>
                    ) : (
                        // For events >15 minutes: title on top, time below, location if available
                        <>
                            <p className="text-sm font-semibold truncate w-[90%]">{item.title}</p>
                            <div className="flex items-center gap-[5px] text-xs opacity-75">
                                <p>{formatTime(new Date(item.plannedAt), 'h:mmaaa')}</p>
                                <span>-</span>
                                <p>{formatTime(endTime, 'h:mmaaa')}</p>
                            </div>
                            {item.location && <p className="text-xs opacity-60 truncate">{item.location}</p>}
                        </>
                    )}
                </div>
            </div>
        );
    }
    */

    // Set minimum height of 20px for very short events
    const isShortEvent = item.duration <= 15;
    const eventHeight = Math.max((item.duration / 60) * blockHeight, 20);

    // Calculate line clamp based on available height
    // Account for checkbox, padding, and time display

    const lineClamp = getLineClamp(item?.duration || 0);

    const zoomScale = (settings.assignments_zoom || 100) / 100;
    const titleFontSize = Math.max(8, isShortEvent ? Math.round(11 * zoomScale) : Math.round(12 * zoomScale));
    const titleLineHeight = Math.max(9, isShortEvent ? Math.round(12 * zoomScale) : Math.round(13 * zoomScale));
    const timeFontSize = Math.max(7, Math.round(10 * zoomScale));

    const handleAssignmentClick = (event: MouseEvent<HTMLDivElement>) => {
        // Ignore clicks on buttons (status toggle/remove)
        if ((event.target as HTMLElement)?.closest('button')) return;
        const matchingDueItem = dueItems?.find((dueItem: any) => String(dueItem?.current?.id) === String(item.id));
        const assignmentForEditor = matchingDueItem || {
            current: {
                id: item.id,
                title: item.title,
                course: item.course,
                plannedAt: item.plannedAt,
                duration: item.duration,
                status: item.status,
            },
            original: {
                id: item.id,
                title: item.title,
                course: item.course,
                plannedAt: item.plannedAt,
                duration: item.duration,
                status: item.status,
            },
        };
        loadSharedComponent("AssignmentCreator", { assignment: assignmentForEditor });
    };

    return (
        <div className="absolute left-0 w-full bg-theme-0 p-[2px] rounded-lg cursor-grab" style={{ opacity: activeCalendarDragItem?.id === item.id ? 0 : 1, top: (getRowFromTime(item.plannedAt) * blockHeight) + 'px', "height": eventHeight + 'px' }}>
            <DraggableTop />
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                className="overflow-hidden max-h-full h-full flex items-start gap-1 bg-theme-1 rounded-md px-[5px] relative z-10"
                onClick={handleAssignmentClick}
            >
                <div className="absolute top-0 left-0 w-full h-full rounded-md z-[-1]" style={backgroundStyle}></div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        const status = localStatus;
                        const nextStatus =
                            status === "complete" || status === "graded"
                                ? "incomplete"
                                : status === "incomplete"
                                    ? "in-progress"
                                    : "complete";

                        setLocalStatus(nextStatus);

                        const plannedPayload = {
                            id: item?.id,
                            status: nextStatus,
                            plannedAt: item?.plannedAt,
                            duration: item?.duration,
                            course: item?.course,
                            title: item?.title,
                        };

                        const matchingDue = dueItems?.find((dueItem: any) => String(dueItem?.current?.id) === String(item.id));
                        const dueId = matchingDue?.current?.id ?? item?.id;
                        if (dueId !== undefined && dueId !== null) {
                            modifyOverrides?.(Number.isNaN(Number(dueId)) ? dueId : Number(dueId), { status: nextStatus });
                        }
                        // Always update the planned entry so the UI reflects the new status immediately
                        if (plannedPayload.id !== undefined && plannedPayload.id !== null) {
                            modifyPlanned?.(plannedPayload as any);
                        }
                    }}
                    className="mt-1 cursor-pointer flex-shrink-0 opacity-75 hover:scale-105 hover:opacity-100 transition-all duration-200"
                    style={{ color: textColor }}
                >
                    {localStatus === "in-progress" ?
                        <ProgressSvg className="w-[14px] h-[14px]" /> :
                        localStatus === "complete" || localStatus === "graded" ?
                            <DoneSvg className="w-[14px] h-[14px]" /> :
                            <IncompleteSvg className="w-[14px] h-[14px]" />
                    }
                </button>
                <div className="flex-1 min-w-0 overflow-hidden" style={{ color: textColor }}>
                    <div className="flex items-start gap-1">
                        <p className={`font-semibold flex-1 overflow-hidden ${localStatus === "complete" || localStatus === "graded" ? "line-through" : ""}`} style={{ fontSize: `${titleFontSize}px`, lineHeight: `${titleLineHeight}px`, display: '-webkit-box', WebkitLineClamp: lineClamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{item.title}</p>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button className="shrink-0 cursor-pointer hover:scale-105 transition-all duration-200" onClick={async () => await removeFromCalendar(item)}>
                                    <Undo2 className="w-4 h-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="mb-2">
                                <span>Remove from calendar</span>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-[5px] overflow-hidden whitespace-nowrap" style={{ fontSize: `${timeFontSize}px`, lineHeight: `${timeFontSize + 2}px`, color: textColor }}>
                        <p>{formatTime(startTime, 'h:mmaaa')}</p>
                        <span>-</span>
                        <p>{formatTime(endTime, 'h:mmaaa')}</p>
                    </div>
                </div>
            </div>
            <DraggableBottom />
        </div>
    )
}


export const CalendarScheduleItem = ({ item, course, dueItemsWithin = [], portalContainer, compact = false }: { item: any, course: any, dueItemsWithin?: any[], portalContainer?: HTMLElement | null | undefined, compact?: boolean }) => {

    const { blockHeight, settings } = usePlanner();
    const { courseData } = useCourses();
    const { loadSharedComponent } = useCommunication();

    const duration = Math.abs(item.end - item.start);
    // Set minimum height of 20px for very short events
    const height = Math.max((duration / 60) * blockHeight, 20);
    const top = (item.start / 60) * blockHeight;

    const code = course?.code !== "" ? course?.code : course?.default;

    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate(), Math.floor(item.start / 60), item.start % 60);
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate(), Math.floor(item.end / 60), item.end % 60);

    const zoomScale = (settings.assignments_zoom || 100) / 100;
    const codeFontSize = Math.max(8, Math.round(14 * zoomScale));
    const timeFontSize = Math.max(7, Math.round(12 * zoomScale));

    const hasDue = dueItemsWithin && dueItemsWithin.length > 0;

    const renderDueMenu = () => {
        if (!hasDue) return null;

        const triggerClasses = compact
            ? "mt-auto w-full text-[11px] font-semibold rounded-md px-[5px] py-[3px] border border-theme bg-theme-0 text-theme-0 hover:bg-theme-1 transition-colors flex items-center justify-between"
            : "mt-auto w-full text-[11px] font-semibold rounded-md px-[6px] py-[4px] border border-theme bg-theme-0 text-theme-0 hover:bg-theme-1 transition-colors flex items-center justify-between";

        const contentClasses = compact ? "w-[200px] p-[6px]" : "w-[240px] p-[8px]";
        const headerMargin = compact ? "mb-[6px]" : "mb-[8px]";
        const listGap = compact ? "gap-[4px]" : "gap-[6px]";
        const chipClasses = compact
            ? "flex items-center gap-[4px] rounded-md px-[5px] py-[3px] border border-theme bg-theme-0 text-theme-0 text-left w-full min-w-0 cursor-pointer hover:brightness-95 transition-all duration-150"
            : "flex items-center gap-[4px] rounded-md px-[6px] py-[4px] border border-theme bg-theme-0 text-theme-0 text-left w-full min-w-0 cursor-pointer hover:brightness-95 transition-all duration-150";
        const titleSize = compact ? "text-[11px]" : "text-[12px]";
        const timeSize = compact ? "text-[9px]" : "text-[10px]";
        const headerTitleSize = compact ? "text-xs" : "text-sm";
        const headerCountSize = compact ? "text-[10px]" : "text-xs";

        return (
            <Popover>
                <PopoverTrigger asChild>
                    <button className={triggerClasses}>
                        <span>Due During Class</span>
                        <span className="text-theme-1">{dueItemsWithin.length}</span>
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    align="center"
                    sideOffset={8}
                    avoidCollisions={false}
                    className={contentClasses}
                    zIndex={10}
                    container={portalContainer || undefined}
                >
                    <div className={`flex items-center justify-between ${headerMargin}`}>
                        <p className={`${headerTitleSize} font-semibold`}>Due during class</p>
                        <p className={`${headerCountSize} text-theme-1`}>{dueItemsWithin.length} total</p>
                    </div>
                    <div className={`flex flex-col ${listGap}`}>
                        {dueItemsWithin.map((dueItem: any) => {
                            const dueDate = dueItem.current?.due ? new Date(dueItem.current.due) : null;
                            const courseColor = courseData?.[dueItem.current?.course || ""]?.color || "gray";
                            let background = "rgba(0,0,0,0.04)";
                            try {
                                background = Color(courseColor).alpha(0.14).string();
                            } catch (e) { /* noop */ }
                            return (
                                <button
                                    key={`class-due-${dueItem.current?.id}`}
                                    className={chipClasses}
                                    style={{ borderColor: courseColor, background }}
                                    onClick={() => loadSharedComponent("AssignmentCreator", { assignment: dueItem })}
                                >
                                    <span className="h-[10px] w-[10px] rounded-full flex-shrink-0" style={{ background: courseColor }} />
                                    <span className={`flex-1 min-w-0 font-semibold truncate ${titleSize}`}>{dueItem.current?.title || "Due item"}</span>
                                    {dueDate && <span className={`${timeSize} text-theme-1 whitespace-nowrap`}>{formatTime(dueDate, "h:mmaaa")}</span>}
                                </button>
                            );
                        })}
                    </div>
                </PopoverContent>
            </Popover>
        );
    };

    return (
        <div className="absolute left-0 w-full bg-theme-0 rounded-lg p-[2px] z-10" style={{ "top": top + 'px', "height": height + 'px' }}>
            <div className="max-h-full h-full flex flex-col flex-wrap gap-1 m-[var(--block-margin)] bg-theme-1 rounded-lg px-[5px] bg-theme-1 relative z-1 overflow-hidden">
                <div className="flex items-center gap-1 overflow-hidden">
                    <div className="size-2 bg-theme-1 rounded-full flex-shrink-0" style={{ background: course.color || "#000000" }}></div>
                    <p className="font-semibold truncate" style={{ fontSize: `${codeFontSize}px`, lineHeight: `${codeFontSize + 2}px` }}>{code}</p>
                </div>
                <p className="truncate" style={{ fontSize: `${timeFontSize}px`, lineHeight: `${timeFontSize + 2}px` }}>{formatTime(start, "h:mmaaa")} - {formatTime(end, "h:mmaaa")}</p>
                <div className="mt-auto w-full flex flex-col gap-1 pb-[6px]">
                    {renderDueMenu()}
                </div>
            </div>
        </div>
    )
}

export const TodayIndicator = () => {

    const { blockHeight } = usePlanner();

    const now = new Date();
    const top = (now.getHours() + (now.getMinutes() / 60)) * blockHeight;

    return (
        <div className="absolute top-0 left-0 w-full h-[2px] bg-theme-link z-[2] pointer-events-none" style={{ top: top + 'px' }}>
            <div className="h-[10px] w-[10px] bg-theme-link rounded-[5px] absolute top-[-5px] left-[-5px]"></div>
        </div>
    )

}
